'use client'

import { ReactNode, useEffect, useState } from 'react'

import { Icon } from '@/app/components/Icon'
import scrollLock from '@/app/hooks/useScrollLock'
import { cn } from '@/app/utils/style'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  className?: string
  direction?: 'left' | 'right' | 'top' | 'bottom'
  width?: string
  height?: string
  closeOnEscape?: boolean
  variant?: 'slide' | 'centered'
  showBackdrop?: boolean
  showCloseIcon?: boolean
  showOnce?: {
    storageKey: string
    storageType: 'localStorage' | 'sessionStorage'
  }
}

export const Modal = function ({
  isOpen,
  onClose,
  children,
  className,
  direction = 'left',
  width = 'w-full',
  height = 'h-[calc(100vh-42px)]',
  closeOnEscape = true,
  variant = 'slide',
  showBackdrop = false,
  showCloseIcon = false,
  showOnce,
}: ModalProps) {
  const [shouldShow, setShouldShow] = useState(true)

  useEffect(() => {
    if (!showOnce) return

    const { storageKey, storageType } = showOnce
    const storage = storageType === 'sessionStorage' ? sessionStorage : localStorage
    const hasShown = storage.getItem(storageKey)

    if (hasShown === 'true') {
      setShouldShow(false)
    } else if (isOpen) {
      storage.setItem(storageKey, 'true')
    }
  }, [showOnce, isOpen])

  useEffect(() => {
    if (shouldShow) {
      scrollLock(isOpen)
    }
  }, [isOpen, shouldShow])

  useEffect(() => {
    if (!closeOnEscape || !shouldShow) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleEscape)
    }

    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, closeOnEscape, onClose, shouldShow])

  useEffect(() => {
    if (!shouldShow) return

    const handleResize = () => {
      scrollLock(isOpen)
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
    }
  }, [isOpen, shouldShow])

  if (!shouldShow) {
    return null
  }

  if (variant === 'centered') {
    return (
      <>
        {showBackdrop && (
          <div
            className={cn(
              'fixed inset-0 z-51 bg-modal-backdrop transition-opacity duration-300',
              isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
            onClick={onClose}
            aria-hidden="true"
          />
        )}

        <div
          className={cn(
            'fixed inset-0 z-52 flex items-center justify-center p-4xl transition-opacity duration-300',
            isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
          onClick={onClose}
        >
          <div
            className={cn(
              'bg-neutral-100 rounded-2xl p-5xl overflow-y-auto max-h-full relative',
              'transition-transform duration-300',
              isOpen ? 'scale-100' : 'scale-95',
              className,
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {showCloseIcon && (
              <button
                onClick={onClose}
                className="absolute z-2 top-5xl right-5xl mt-[-3px] mr-[-3px] text-neutral-900 hover:text-primary-600 active:text-primary-200 transition-colors cursor-pointer"
                aria-label="Close modal"
                type="button"
              >
                <Icon provider="fontawesome" name="faClose" size={25} />
              </button>
            )}
            {children}
          </div>
        </div>
      </>
    )
  }

  const directionClasses = {
    left: {
      container: 'left-0 top-[42px]',
      transform: isOpen ? 'translate-x-0' : '-translate-x-full',
    },
    right: {
      container: 'right-0 top-[42px]',
      transform: isOpen ? 'translate-x-0' : 'translate-x-full',
    },
    top: {
      container: 'top-0 inset-x-0',
      transform: isOpen ? 'translate-y-0' : '-translate-y-full',
    },
    bottom: {
      container: 'bottom-0 inset-x-0',
      transform: isOpen ? 'translate-y-0' : 'translate-y-full',
    },
  }

  const config = directionClasses[direction]

  return (
    <div
      className={cn(
        'fixed inset-x-0 z-51 lg:hidden transition-transform duration-300 ease-in-out',
        config.container,
        config.transform,
        className,
      )}
    >
      <div className={cn(width, height, 'bg-white overflow-y-auto flex flex-col')}>{children}</div>
    </div>
  )
}
